import React, { useCallback, useEffect, useState } from 'react';
import { TablePagination } from '@components/TablePagination/TablePagination';

export const ROWS_PER_PAGE_ITEMS = [10, 30, 50];
export const ROWS_PER_PAGE = 10;
export const DEFAULT_PAGE = 1;

export interface RenderPaginationProps {
  totalResultRows: number;
  customRowsPerPageText?: string;
  finalTotalResultIsKnown?: boolean;
  isLastPage?: boolean;
  onIncrementCallback?: () => void;
  onDecrementCallback?: () => void;
  onLimitChangeCallback?: (value: number) => void;
}

export const usePagination = (
  rowsPerPageItems: number[] = ROWS_PER_PAGE_ITEMS,
  rowsPerPage: number = ROWS_PER_PAGE,
) => {
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(rowsPerPage);

  useEffect(() => {
    setPage(1);
  }, [limit]);

  const renderPagination = useCallback(
    ({
      totalResultRows,
      customRowsPerPageText,
      finalTotalResultIsKnown = false,
      isLastPage = false,
      onIncrementCallback,
      onDecrementCallback,
      onLimitChangeCallback,
    }: RenderPaginationProps) => {
      const onIncrement = () => {
        setPage(prevPage => prevPage + 1);
        onIncrementCallback?.();
      };

      const onDecrement = () => {
        setPage(prevPage => prevPage - 1);
        onDecrementCallback?.();
      };

      return (
        <TablePagination
          pagination={{
            totalResultRows,
            page,
            finalTotalResultIsKnown,
            isLastPage,
            onIncrement,
            onDecrement,
          }}
          limit={limit}
          rowsPerPage={{
            items: rowsPerPageItems,
            onValueChange: newValue => {
              setLimit(newValue);
              onLimitChangeCallback?.(newValue);
            },
            customText: customRowsPerPageText,
          }}
        />
      );
    },
    [page, limit, rowsPerPageItems],
  );

  return { renderPagination, page, limit, setPage };
};
